import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/i18n";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { FileText, DoorOpen, Sparkles, ClipboardCheck, Repeat, Building2, CalendarDays } from "lucide-react";

const typeBadge = (ty) =>
  ty === "checkout"
    ? { bg: "#DBEAFE", fg: "#1D4ED8", label: "Check-out" }
    : { bg: "#F9F3E5", fg: "#B8860B", label: "Service" };

function KpiCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[var(--border-warm)] rounded-lg p-6 relative overflow-hidden fade-up-2">
      <div className="absolute -right-4 -top-4 w-24 h-24 bg-[var(--gold-light)] rounded-full opacity-70" />
      <div className="relative flex items-start justify-between">
        <div>
          <div className="hotel-overline">{label}</div>
          <div className="font-display text-4xl text-[var(--navy)] mt-2 font-mono-hotel">{value}</div>
          {sub && <div className="text-xs text-[var(--ink-2)] mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-md bg-[var(--navy)] text-[var(--gold)] flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function ManagementDashboard() {
  const [stats, setStats] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [properties, setProperties] = useState([]);
  const [datePreset, setDatePreset] = useState("today");
  const [customDate, setCustomDate] = useState("");
  const [filterProperty, setFilterProperty] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterRoom, setFilterRoom] = useState("");

  const load = () => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/inspections", { params: { limit: 2000 } }),
      api.get("/config/properties"),
    ])
      .then(([a, b, c]) => {
        setStats(a.data);
        setInspections(b.data);
        setProperties(c.data.properties);
      })
      .catch(() => toast.error("Failed to load dashboard"));
  };

  useEffect(() => { load(); }, []);

  const selectedDate = useMemo(() => {
    if (datePreset === "custom" && customDate) return customDate;
    const d = new Date();
    if (datePreset === "yesterday") d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }, [datePreset, customDate]);

  const dayRows = useMemo(
    () => inspections.filter((r) => r.created_at.startsWith(selectedDate)),
    [inspections, selectedDate]
  );

  const dayKpis = useMemo(() => {
    const checkout = dayRows.filter((r) => r.service_type === "checkout").length;
    const service = dayRows.filter((r) => r.service_type === "service").length;
    const roomCounts = {};
    dayRows.forEach((r) => {
      const k = `${r.property_id}:${r.room_number}`;
      roomCounts[k] = (roomCounts[k] || 0) + 1;
    });
    return {
      cleanings: dayRows.length,
      checkout,
      service,
      roomsCovered: Object.keys(roomCounts).length,
      multiClean: Object.values(roomCounts).filter((c) => c >= 2).length,
    };
  }, [dayRows]);

  const perHotel = useMemo(() => {
    return (stats?.per_property || []).map((p) => {
      const rows = dayRows.filter((r) => r.property_id === p.property_id);
      return {
        ...p,
        day_cleanings: rows.length,
        day_checkout: rows.filter((r) => r.service_type === "checkout").length,
        day_service: rows.filter((r) => r.service_type === "service").length,
        day_rooms: new Set(rows.map((r) => r.room_number)).size,
      };
    });
  }, [stats, dayRows]);

  const filtered = useMemo(() => {
    return dayRows.filter((r) => {
      if (filterProperty !== "all" && r.property_id !== filterProperty) return false;
      if (filterType !== "all" && r.service_type !== filterType) return false;
      if (filterRoom.trim() && !r.room_number.includes(filterRoom.trim())) return false;
      return true;
    });
  }, [dayRows, filterProperty, filterType, filterRoom]);

  if (!stats) {
    return (
      <Layout title="Group Dashboard" subtitle="Owner · All Properties">
        <div className="hotel-overline">Loading metrics…</div>
      </Layout>
    );
  }

  const dateLabel = datePreset === "today" ? "Today" : datePreset === "yesterday" ? "Yesterday" : selectedDate;

  return (
    <Layout title="Group Dashboard" subtitle="Owner · All Tamanna Properties · One View">
      {/* Date filter */}
      <div className="bg-white border border-[var(--border-warm)] rounded-lg p-4 mb-6 flex flex-wrap items-center gap-3 no-print">
        <div className="flex items-center gap-2 mr-2">
          <CalendarDays className="w-4 h-4 text-[var(--gold)]" />
          <span className="hotel-overline">Report date</span>
        </div>
        <button
          type="button"
          data-testid="date-filter-today"
          onClick={() => setDatePreset("today")}
          className={`sop-pill px-5 h-10 rounded-md border-2 text-sm font-semibold ${
            datePreset === "today" ? "bg-[var(--navy)] border-[var(--navy)] text-white" : "bg-white border-[var(--border-warm)] text-[var(--ink-2)] hover:border-[var(--navy)]"
          }`}
        >
          Today
        </button>
        <button
          type="button"
          data-testid="date-filter-yesterday"
          onClick={() => setDatePreset("yesterday")}
          className={`sop-pill px-5 h-10 rounded-md border-2 text-sm font-semibold ${
            datePreset === "yesterday" ? "bg-[var(--navy)] border-[var(--navy)] text-white" : "bg-white border-[var(--border-warm)] text-[var(--ink-2)] hover:border-[var(--navy)]"
          }`}
        >
          Yesterday
        </button>
        <button
          type="button"
          data-testid="date-filter-custom"
          onClick={() => setDatePreset("custom")}
          className={`sop-pill px-5 h-10 rounded-md border-2 text-sm font-semibold ${
            datePreset === "custom" ? "bg-[var(--navy)] border-[var(--navy)] text-white" : "bg-white border-[var(--border-warm)] text-[var(--ink-2)] hover:border-[var(--navy)]"
          }`}
        >
          Custom date
        </button>
        {datePreset === "custom" && (
          <Input
            data-testid="custom-date-input"
            type="date"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            className="h-10 bg-white border-[var(--border-warm)] w-44"
          />
        )}
        <span data-testid="selected-date-label" className="ml-auto text-sm font-mono-hotel text-[var(--ink-2)]">{dateLabel}</span>
      </div>

      {/* KPIs (selected date) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <KpiCard testid="kpi-today-cleanings" label={`Rooms Cleaned · ${dateLabel}`} value={dayKpis.cleanings} sub={`${dayKpis.roomsCovered} unique rooms`} icon={ClipboardCheck} />
        <KpiCard testid="kpi-checkout-today" label="Check-out Rooms Cleaned" value={dayKpis.checkout} sub={`${dateLabel} · all properties`} icon={DoorOpen} />
        <KpiCard testid="kpi-service-today" label="Service Rooms Cleaned" value={dayKpis.service} sub={`${dateLabel} · all properties`} icon={Sparkles} />
        <KpiCard testid="kpi-multi-clean" label="Rooms Cleaned 2+ Times" value={dayKpis.multiClean} sub="Repeat cleanings" icon={Repeat} />
      </div>

      {/* Per-hotel cards (selected date) */}
      <div className="mb-8">
        <div className="hotel-overline mb-3">Per-Hotel Performance · {dateLabel}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {perHotel.map((p) => (
            <div key={p.property_id} data-testid={`property-card-stats-${p.property_id}`} className="bg-white border border-[var(--border-warm)] rounded-lg p-5 hover:border-[var(--gold)] transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-md bg-[var(--navy)] text-[var(--gold)] flex items-center justify-center">
                  <Building2 className="w-4 h-4" />
                </div>
                <div>
                  <div className="hotel-overline">{p.code}</div>
                  <div className="font-display text-lg text-[var(--navy)] leading-tight">{p.name.replace(" Hotel", "")}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="bg-[var(--surface-elev)] rounded-md p-2">
                  <div className="font-mono-hotel text-xl font-bold text-[var(--navy)]">{p.day_checkout}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">Check-out</div>
                </div>
                <div className="bg-[var(--surface-elev)] rounded-md p-2">
                  <div className="font-mono-hotel text-xl font-bold text-[var(--navy)]">{p.day_service}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">Service</div>
                </div>
              </div>
              <div className="text-xs text-[var(--ink-2)] mt-3">
                {p.day_rooms}/{p.total_rooms} rooms covered
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <div className="lg:col-span-2 bg-white border border-[var(--border-warm)] rounded-lg p-6">
          <div className="hotel-overline">Trend</div>
          <h3 className="font-display text-2xl text-[var(--navy)] mb-4">7-Day Cleaning Volume</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.daily_trend} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDEAE2" />
                <XAxis dataKey="date" stroke="#8A94A6" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#8A94A6" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0F1A2C", border: "none", borderRadius: 8, color: "#fff" }} />
                <Legend />
                <Bar dataKey="checkout" name="Check-out" stackId="a" fill="#1D4ED8" radius={[0, 0, 0, 0]} />
                <Bar dataKey="service" name="Service" stackId="a" fill="#B8860B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-[var(--border-warm)] rounded-lg p-6">
          <div className="hotel-overline">By shift</div>
          <h3 className="font-display text-2xl text-[var(--navy)] mb-4">Cleaning Volume</h3>
          <div className="space-y-4">
            {stats.shift_counts.map((s) => {
              const max = Math.max(...stats.shift_counts.map((x) => x.count), 1);
              return (
                <div key={s.shift}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-[var(--ink)]">{s.shift.split("(")[0].trim()}</span>
                    <span className="font-mono-hotel font-semibold text-[var(--navy)]">{s.count}</span>
                  </div>
                  <div className="h-2 bg-[var(--border-subtle)] rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-[var(--gold)] transition-all" style={{ width: `${(s.count / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cleaning log (selected date) */}
      <div className="bg-white border border-[var(--border-warm)] rounded-lg overflow-hidden">
        <div className="p-6 border-b border-[var(--border-subtle)] bg-[var(--surface-elev)] flex flex-wrap items-end gap-4 justify-between">
          <div>
            <div className="hotel-overline">Audit Trail</div>
            <h3 className="font-display text-2xl text-[var(--navy)]">Cleaning Log · {dateLabel}</h3>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="hotel-overline block mb-1">Property</label>
              <Select value={filterProperty} onValueChange={setFilterProperty}>
                <SelectTrigger data-testid="filter-property-select" className="w-44 h-10 bg-white border-[var(--border-warm)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All properties</SelectItem>
                  {properties.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`filter-property-option-${p.id}`}>{p.name.replace(" Hotel", "")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="hotel-overline block mb-1">Type</label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type-select" className="w-36 h-10 bg-white border-[var(--border-warm)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="checkout" data-testid="filter-type-option-checkout">Check-out</SelectItem>
                  <SelectItem value="service" data-testid="filter-type-option-service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="hotel-overline block mb-1">Room</label>
              <Input data-testid="filter-room-input" value={filterRoom} onChange={(e) => setFilterRoom(e.target.value)} placeholder="e.g. 401" className="h-10 bg-white border-[var(--border-warm)] w-28" />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="inspection-log-table">
            <TableHeader>
              <TableRow className="bg-[var(--surface-elev)]/60 hover:bg-[var(--surface-elev)]/60">
                <TableHead>Date & Time (24h)</TableHead>
                <TableHead>Property</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Guest</TableHead>
                <TableHead>Supervisor</TableHead>
                <TableHead>Shift</TableHead>
                <TableHead className="text-right">Report</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-[var(--muted)] py-8">No records for {dateLabel}.</TableCell></TableRow>
              )}
              {filtered.map((r) => {
                const tb = typeBadge(r.service_type);
                return (
                  <TableRow key={r.id} data-testid={`inspection-row-${r.id}`}>
                    <TableCell className="font-mono-hotel text-xs">{fmtDateTime(r.created_at)}</TableCell>
                    <TableCell className="text-[var(--ink-2)]">{r.property_name?.replace(" Hotel", "")}</TableCell>
                    <TableCell className="font-mono-hotel font-semibold">{r.room_number}</TableCell>
                    <TableCell>
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: tb.bg, color: tb.fg }}>{tb.label}</span>
                    </TableCell>
                    <TableCell>{r.guest_name} <span className="text-xs text-[var(--muted)]">×{r.guest_count}</span></TableCell>
                    <TableCell>{r.supervisor_name}</TableCell>
                    <TableCell className="text-[var(--ink-2)]">{r.shift.split("(")[0].trim()}</TableCell>
                    <TableCell className="text-right">
                      <Link to={`/inspections/${r.id}`}>
                        <Button variant="outline" size="sm" data-testid={`view-report-btn-${r.id}`} className="border-[var(--border-warm)]">
                          <FileText className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
